// The publish bar: what the link serves against what the editor holds, and
// the one thing that moves the first onto the second.
//
// A workflow has a DRAFT — the fields the editor writes, which every save
// moves — and a PUBLISHED VERSION, which is what a visitor at the share link
// is asked. Publishing freezes the draft as the next version; every version
// is kept, whole and immutable, and every response records the one it was
// given against. This bar says which version is live and whether the draft
// has moved since, and offers Publish only when it has — the API refuses a
// publish of an unchanged draft, and a button that would only ever be refused
// is better dark.
import React from "react";
import { Alert, Badge } from "reactstrap";
import { useMutation } from "@apollo/client";
import * as Notify from "@repoflow/form-schema/notify";
import { Icon as LoadingIcon } from "@repoflow/components/loading";
import * as WorkflowQL from "queries/workflow";

export const when = (iso) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

// Drop every versions listing from the cache: a publish is a row the listing
// never held, and `list-cursor` accumulates pages under the listing's id.
const evictVersions = (cache) => {
  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes("/versions")) cache.data.delete(key);
  });
};

export const usePublish = (workflowId) => {
  const [publish, { loading }] = useMutation(WorkflowQL.publish, {
    update: evictVersions,
    // The answer carries the workflow under the same cache id; the refetch is
    // what makes the route's Section query agree with it without a
    // navigation, the way the Share tab's does.
    refetchQueries: [{ query: WorkflowQL.get, variables: { workflowId } }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const { publishedVersion } = data.viewer.account.workflows.get.publish;
      Notify.success(`Version ${publishedVersion} published.`);
    },
    onError: (error) => {
      console.error("workflow.publish:", error);
      const message = String(error.message || "");
      Notify.error(
        message.includes("unchanged")
          ? "Nothing to publish: the draft is what is already published."
          : message.includes("conflict")
            ? "Somebody else published this workflow a moment ago. Reload and look again."
            : "The workflow could not be published. Please try again."
      );
    },
  });

  return { publish: () => publish({ variables: { workflowId } }), loading };
};

export const PublishBar = ({ workflow }) => {
  const { workflowId, changed, published } = workflow;
  const publishedVersion = workflow.publishedVersion || 0;
  const { publish, loading } = usePublish(workflowId);

  return (
    <Alert
      id="workflow-publish"
      color={publishedVersion ? (changed ? "warning" : "light") : "info"}
      className="d-flex justify-content-between align-items-center mt-3 mb-0"
    >
      <span id="workflow-publish-status">
        {publishedVersion ? (
          <>
            <strong>Version {publishedVersion}</strong> is published
            {published && published.publishedAt ? ` (${when(published.publishedAt)})` : ""}.{" "}
            {changed ? (
              <Badge color="warning" pill id="workflow-publish-changed">
                Unpublished changes
              </Badge>
            ) : (
              <span className="text-muted">The draft is what is published.</span>
            )}
          </>
        ) : (
          <>
            <strong>Not published.</strong> The share link opens nothing until
            the workflow is published.
          </>
        )}
      </span>
      <button
        type="button"
        id="workflow-publish-button"
        className="btn btn-success btn-sm"
        disabled={loading || !changed}
        title={changed ? "Freeze the draft as the next version" : "Nothing has changed since the last publish"}
        onClick={publish}
      >
        {loading ? <LoadingIcon /> : publishedVersion ? `Publish version ${publishedVersion + 1}` : "Publish"}
      </button>
    </Alert>
  );
};
